import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { LANGUAGE_NAMES, SupportedLanguage, SUPPORTED_LANGUAGES } from "@/constants/languages";
import { createLoginSchema } from "@/features/auth/schema/loginSchema";
import { TLoginForm } from "@/features/auth/types/loginForm";
import { useLanguage } from "@/providers/LanguageProvider";
import { AppRoutes } from "@/routes/config";
import { getQueryFromURL } from "@/utils/url";
import { isSafeRedirectUrl } from "@/utils/redirect";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { text } from "@/theme/colors";
import { TOption } from "@/types/common";
import { loginApi } from "@/api/services/main/auth";
import { showError } from "@/utils/error";
import { saveStoredLanguage } from "@/utils/language-storage";

type TQuery = {
  learnerId?: string;
  redirectUrl?: string;
};

export const LoginForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = getQueryFromURL<TQuery>();
  const { currentLanguage, changeLanguage } = useLanguage();
  const isEn = currentLanguage === "en";
  const [isLoading, setIsLoading] = useState(false);

  const learnerId = query?.learnerId ?? "";
  const redirectUrl = query?.redirectUrl;
  const loginSchema = useMemo(() => createLoginSchema(t), [t]);

  const defaultValues = useMemo((): TLoginForm => {
    return {
      learnerId: learnerId,
      password: "",
    };
  }, [learnerId]);

  const methods = useForm<TLoginForm>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(loginSchema),
  });
  const { control } = methods;

  const onSubmit = async (formData: TLoginForm) => {
    try {
      setIsLoading(true);
      const response = await loginApi({ accountId: formData.learnerId, password: formData.password, languageCode: currentLanguage });
      const { isFirstLogin } = response.data;

      // Persist only now — the select on this screen changes the language in memory only, so a
      // failed / abandoned login leaves no stored preference behind
      saveStoredLanguage(currentLanguage);

      if (isFirstLogin) {
        const params = redirectUrl && isSafeRedirectUrl(redirectUrl) ? `?redirectUrl=${encodeURIComponent(redirectUrl)}` : "";
        navigate(`${AppRoutes.privacyConsent}${params}`);
      } else {
        navigate(isSafeRedirectUrl(redirectUrl) ? redirectUrl! : AppRoutes.home);
      }
    } catch (error) {
      showError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const languageOptions: TOption[] = SUPPORTED_LANGUAGES.map((lang) => ({
    value: lang,
    label: LANGUAGE_NAMES[lang],
  }));

  const handleLanguageChange = (value: string) => {
    changeLanguage(value as SupportedLanguage);
  };

  useEffect(() => {
    const errorFields = Object.keys(methods.formState.errors) as (keyof TLoginForm)[];
    if (errorFields.length > 0) {
      methods.trigger(errorFields);
    }
  }, [methods.formState.errors, methods.trigger, currentLanguage]);

  return (
    <Box
      sx={{
        mx: 2,
      }}
    >
      <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={loginSchema}>
        <Stack>
          <Stack gap={2}>
            <Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="cap12" color={text.secondary}>
                  {t("login.learnerId")}
                  {!isEn && " / Learner ID"} <span style={{ color: text.error }}>*</span>
                </Typography>
              </Box>
              <FormField control={control} name="learnerId" noLabel>
                <Input size="lg" placeholder={t("login.learnerIdInputPlaceholder")} readonly={!!learnerId} />
              </FormField>
            </Box>
          </Stack>

          <Stack gap={2} sx={{ mt: 2 }}>
            <Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="cap12" color={text.secondary}>
                  {t("login.password")}
                  {!isEn && " / Password"} <span style={{ color: text.error }}>*</span>
                </Typography>
              </Box>
              <FormField control={control} name="password" noLabel>
                <Input type="password" size="lg" placeholder="" />
              </FormField>
            </Box>

            <Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="cap12" color={text.secondary}>
                  {t("login.language")}
                  {!isEn && " / Language"} <span style={{ color: text.error }}>*</span>
                </Typography>
              </Box>
              <Select size="lg" options={languageOptions} value={currentLanguage} onChange={handleLanguageChange} clearable={false} nonControl />
            </Box>
          </Stack>

          <Box sx={{ mt: 3 }}>
            <Button type="submit" variant="primary" fullWidth size="sm" isLoading={isLoading} disabled={isLoading}>
              {t("login.submit")}
              {!isEn && " / Login"}
            </Button>
          </Box>
        </Stack>
      </Form>
    </Box>
  );
};
